// @flow
import * as React from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {ButtonLink, Button} from 'sail/Button';
import {Text} from 'sail/Text';
import {DocsIcon, CardGroupQuickLinks} from 'docs/components';
import {usePrefs, useVideos} from 'docs/data';

import './VideoHeroSection.scss';

type VideoHeroSectionProps = {|
  children: React.Node,
  videoTitle: string,
  linkHref: string,
  imageSrc: string,
  quickLinks?: Array<{|label: string, href: string|}>,
  useButton?: boolean,
  playlistName: string,
|};

export const VideoHeroSection = ({
  children,
  videoTitle,
  linkHref,
  imageSrc,
  useButton,
  quickLinks,
  playlistName,
}: VideoHeroSectionProps) => {
  const videos = useVideos({prefetchAllVideos: true});
  const {getPref} = usePrefs();

  const button = useButton ? (
    <Button
      icon={'arrowRight'}
      iconPosition="right"
      label={'Video'}
      href={linkHref}
      size="medium"
      color="blue"
    />
  ) : (
    <ButtonLink
      icon={'arrowRight'}
      iconPosition="right"
      color="blue"
      label={'Video'}
      size="large"
      href={linkHref}
    />
  );

  const fetchedTopic = videos.getTopic(videoTitle);
  if (!fetchedTopic) {
    return '';
  }

  const serverLang = getPref('lang') || 'ruby';

  const prefMatchVideo =
    fetchedTopic?.videos.find((video) => {
      return video.prefs.lang === serverLang;
    }) || fetchedTopic?.defaultVideo;

  const descriptionExcerpt = prefMatchVideo.description?.split('.')[0];
  const truncatedTitle = prefMatchVideo.title.substring(
    0,
    prefMatchVideo.title.indexOf(' with') > 0
      ? prefMatchVideo.title.indexOf(' with')
      : prefMatchVideo.title.length,
  );

  return (
    <Box
      className="VideoHeroSection"
      flex={{direction: 'row', alignItems: 'center'}}
      divider={{color: 'light', width: 1, side: 'bottom'}}
      margin={{bottom: 16}}
    >
      <Box flexGrow={1} className="VideoHeroSection-left">
        <Box padding={{bottom: 20}}>
          <Text className="VideoHeroSection-badge">{playlistName}</Text>
          <Text className="VideoHeroSection-badge">{serverLang}</Text>
        </Box>

        <Text size={24} weight="bold" color="dark">
          {`${truncatedTitle} with `}
        </Text>

        {/* below would later become language selector that would update prefs */}
        <Text size={24} weight="bold" color="blue">
          {`stripe-${serverLang}`}
        </Text>
        <Box className="VideoHeroSection-text">{descriptionExcerpt}</Box>
        <Box flex style={{marginTop: 'auto'}} padding={{bottom: 12}}>
          <Group
            direction="horizontal"
            alignItems="center"
            padding={{top: 12}}
            spacing={8}
          >
            <DocsIcon category="general" icon="video" size={16} />
            <Text>{convertTimeToString(prefMatchVideo.duration || 0)}</Text>
          </Group>
        </Box>
        {button}
        {quickLinks && (
          <Box margin={{top: 24}}>
            <CardGroupQuickLinks links={quickLinks} />
          </Box>
        )}
      </Box>
      <Box flexGrow={2} flex={{justifyContent: 'flex-end'}}>
        <Box id={'banner image'} className="VideoHeroSection-image">
          <img alt="" src={imageSrc} width="100%" />
        </Box>
      </Box>
    </Box>
  );
};

function convertTimeToString(time: number) {
  const minute = Math.floor(time / 60);
  const second = time - minute * 60;

  if (second < 10) {
    return `${minute.toString()}:0${second.toString()}`;
  }

  return `${minute.toString()}:${second.toString()}`;
}
